import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import styled from 'styled-components';

import BrandDoctorNowIcon from '@/components/atom/icon/BrandDoctornow';
import CloseIcon from '@/components/atom/icon/CloseIcon';
import InfoIcon from '@/components/atom/icon/InfoIcon';
import ArrowsLeft from '@/components/atom/icon/arrow/ArrowsLeft';
import Naver from '@/components/atom/icon/login/Naver';
import KakaoLoginIcon from '@/components/atom/icon/login/kakao';
import { KAKAO_AUTH_URL, NAVER_AUTH_URL } from '@/constants/SocialOAuth';
import { DeeplinkLoggerEventName } from '@/constants/logs/deeplink';
import { ReturnUrlState, UrlState } from '@/recoil/atoms/common/auth';
import { useAirbridgeDynamicLink } from '@/shared/hooks';
import { DynamicLinkOptions } from '@/shared/types';
import { DynamicLinkContent } from '@/shared/types/airbridge';
import { Tablet } from '@/styles/layout';
import { isServer } from '@/types/common';
import { getPageName, logger } from '@/utils/logger';
import { isMobile } from '@/utils/mobile/RNPlugins';

const LoginType = {
  SNS: 'sns',
  EMAIL: 'email',
} as const;

export type LoginType = (typeof LoginType)[keyof typeof LoginType];

interface Props {
  isWritePage?: boolean;
  onClick?: () => void;
  redirectUrl?: string;
  trackInfo?: {
    campaign: string;
    content: DynamicLinkContent;
    buttonText: string;
    sub_id?: string;
    sub_id_1?: string;
    sub_id_2?: string;
    sub_id_3?: string;
  };
  customTitle?: string;
  customMessage?: string;
}
const LoginModal = ({ isWritePage = false, onClick, redirectUrl, trackInfo, customTitle, customMessage }: Props) => {
  const [loginType, setLoginType] = useState<LoginType>(LoginType.SNS);
  const router = useRouter();
  const pathName = router.asPath;
  const setPageName = useSetRecoilState<ReturnUrlState>(UrlState);
  const airbridgeDynamicLink = useAirbridgeDynamicLink();

  const dynamicLinkOptions: DynamicLinkOptions = useMemo(() => {
    if (trackInfo) {
      return {
        campaign: trackInfo.campaign,
        content: trackInfo.content,
        term: trackInfo.buttonText,
        ...(trackInfo.sub_id && {
          sub_id: trackInfo.sub_id,
        }),
        ...(trackInfo.sub_id_1 && {
          sub_id_1: trackInfo.sub_id_1,
        }),
        ...(trackInfo.sub_id_2 && {
          sub_id_2: trackInfo.sub_id_2,
        }),
        ...(trackInfo.sub_id_3 && {
          sub_id_3: trackInfo.sub_id_3,
        }),
      };
    }

    if (!isServer && isMobile(window.navigator.userAgent)) {
      return {
        campaign: 'web_all',
        content: 'web_drawer_mdl_all',
        term: '닥터나우 앱 다운로드',
      };
    }

    return {
      campaign: 'web_service',
      content: 'web_lnb_mdl_service',
      term: '닥터나우 앱 다운로드',
    };
  }, [isServer, trackInfo]);

  const title = useMemo(() => {
    if (customTitle) {
      return <h1 className='whitespace-pre-wrap'>{customTitle}</h1>;
    }

    if (loginType === LoginType.SNS) {
      return (
        <h1>
          로그인 및 회원가입을
          <br />
          클릭 한 번으로 시작하세요.
        </h1>
      );
    }
    return <h1>이메일로 로그인하기</h1>;
  }, [loginType, customTitle]);

  const onClickLoginType = (type: LoginType) => {
    setLoginType(type);
  };

  const onClickClose = () => {
    logger({
      pageName: getPageName(router),
      eventName: 'login_exit_click',
    });

    if (isWritePage) {
      router.back();
      return;
    }

    onClick?.();
  };
  const onClickLoginLog = (eventName: string) => {
    logger({
      pageName: getPageName(router),
      eventName,
    });
  };

  useEffect(() => {
    setPageName(redirectUrl || pathName);
  }, [pathName, setPageName, redirectUrl]);

  return (
    <Wrapper>
      <Close>
        <button type='button' onClick={onClickClose}>
          <CloseIcon />
        </button>
      </Close>
      {title}
      <p>{customMessage || '기존 닥터나우 앱과 동일한 아이디를 사용해요.'}</p>
      {loginType === LoginType.SNS ? (
        <>
          <Link href={KAKAO_AUTH_URL}>
            <KaKao onClick={() => onClickLoginLog('kakao_login_click')}>
              <KakaoLoginIcon />
              카카오톡으로 시작하기
            </KaKao>
          </Link>
          <Link href={NAVER_AUTH_URL}>
            <NaverButton onClick={() => onClickLoginLog('naver_login_click')}>
              <Naver />
              네이버로 시작하기
            </NaverButton>
          </Link>
          <AppDownload
            onClick={() => {
              onClickLoginLog('app_download_click');
              airbridgeDynamicLink.navigateWithTracking(DeeplinkLoggerEventName.ClickCtaButton, dynamicLinkOptions);
            }}
          >
            <BrandDoctorNowIcon />
            닥터나우 앱 다운로드
          </AppDownload>

          <Preparation>
            <InfoIcon />
            이메일, 애플 로그인은 앱을 이용해 주세요.
          </Preparation>
        </>
      ) : (
        <Form>
          <LoginButton>로그인</LoginButton>

          <Info>
            <InfoIcon />
            이메일 계정의 회원가입과 아이디/비밀번호 찾기는 닥터나우 앱을 이용해주세요.
          </Info>

          <BackButton type='button' onClick={() => onClickLoginType(LoginType.SNS)}>
            <ArrowsLeft />
            이전으로 돌아가기
          </BackButton>
        </Form>
      )}
    </Wrapper>
  );
};

export default LoginModal;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 20px;
  width: 100%;
  max-width: 400px;
  padding: 32px;
  background-color: white;

  & > h1 {
    ${({ theme }) => theme.typography.UI22SB};
    color: ${({ theme }) => theme.color.gray800};
    margin-bottom: 4px;

    @media screen and (max-width: ${Tablet}) {
      ${({ theme }) => theme.typography.UI20SB};
    }
  }

  & > p:nth-of-type(1) {
    ${({ theme }) => theme.typography.UI16M};
    color: ${({ theme }) => theme.color.gray400};

    @media screen and (max-width: ${Tablet}) {
      ${({ theme }) => theme.typography.UI14M};
    }
  }

  & > button {
    padding: 18px 0;
    color: ${({ theme }) => theme.color.gray800};
    svg {
      margin-right: 8px;
    }
  }

  @media screen and (max-width: ${Tablet}) {
    padding: 24px;
    max-width: 312px;
  }
`;

const Close = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-bottom: 4px;

  & > button {
    padding: 8px;
    border: none;
    border-radius: 4px;

    &:hover {
      background-color: ${({ theme }) => theme.color.gray30};
    }

    &:active {
      background-color: ${({ theme }) => theme.color.gray50};
    }
  }
`;

const KaKao = styled.button`
  display: flex;
  justify-content: center;
  border: none;
  background: #ffe337;
  border-radius: 12px;
  margin-bottom: 8px;
  margin-top: 32px;
`;

const NaverButton = styled(KaKao)`
  background-color: #04cf00;
  margin-top: 0;
  color: ${({ theme }) => theme.color.white} !important;
`;

const AppDownload = styled(KaKao)`
  background: ${({ theme }) => theme.color.gray30};
  border-radius: 12px;
  margin-top: 0;

  &:hover {
    background: ${({ theme }) => theme.color.gray50};
  }

  &:active {
    background: ${({ theme }) => theme.color.gray100};
  }
`;

const Form = styled.form`
  margin-top: 24px;

  & > div {
    margin-bottom: 16px;
  }
`;

const LoginButton = styled.button`
  border: none;
  background-color: ${({ theme }) => theme.color.primary500};
  padding: 17px 0;
  margin-bottom: 16px;
  margin-top: 8px;
  color: ${({ theme }) => theme.color.white};
  border-radius: 12px;
  width: 100%;

  &:hover {
    background-color: ${({ theme }) => theme.color.primary700};
  }
`;

const Info = styled.div`
  display: flex;
  border-radius: 8px;
  padding: 16px 23px 16px 16px;
  border: none;
  background-color: ${({ theme }) => theme.color.gray50};
  ${({ theme }) => theme.typography.UI14M};
  color: ${({ theme }) => theme.color.gray500};
  margin-bottom: 24px;
  svg {
    margin-right: 8px;
  }
`;

const Preparation = styled.p`
  ${({ theme }) => theme.typography.UI14M};
  color: ${({ theme }) => theme.color.gray400};
  margin-top: 32px;
  display: flex;
  align-items: center;

  svg {
    margin-right: 8px;
  }
`;

const BackButton = styled.button`
  width: fit-content;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.gray200};
  margin-top: 16px;

  &:hover {
    background-color: ${({ theme }) => theme.color.gray30};
  }

  &:active {
    background-color: ${({ theme }) => theme.color.gray50};
  }

  svg {
    margin-right: 8px;
  }
`;
